<template>
  <VLimitReached>
    <a-card class="mt-6" size="small">
      <div class="flex space-x-4 mb-3">
        <VButton
            v-if="selectedRowKeys.length && canEditSurvey"
            type="default-negative-hover"
            icon="delete"
            @click="onDeleteResults"
        >
          Remove selected ({{ selectedRowKeys.length }})
        </VButton>
        <VButton @click="clearFilters">
          Clear filters
        </VButton>
      </div>
      <a-config-provider :render-empty="showEmptyData">
        <a-table
            :columns="columns"
            :data-source="tableData"
            :pagination="pagination"
            :scroll="{ x: true }"
            :row-selection="rowSelection"
            row-key="id"
            id="responses-table"
            bordered
            @change="handleChange"
        />
      </a-config-provider>
    </a-card>
  </VLimitReached>
</template>

<script>
import {mapGetters} from "vuex";
import {EmptyTableSvg} from "@/helpers/customIcons";
import VLimitReached from "../../VLimitReached";
import VButton from "../../VButton";
import {RESET_RESULTS} from "../../../store/actions.type";

export default {
  name: "SurveyAnalyticComments",
  components: {VLimitReached, VButton},
  data() {
    return {
      selectedRowKeys: [],
      filteredInfo: null,
      isDeletingResults: false
    }
  },
  computed: {
    ...mapGetters('survey', ["survey", "individualResponses", "isLoadingChild"]),
    ...mapGetters('collaborators', ["canEdit", "permissions"]),
    canEditSurvey() {
      return this.canEdit({surveyId: this.survey._id}) || [this.permissions.admin.value, this.permissions.edit.value].includes(this.survey.folderPermission);
    },
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: this.onSelectChange,
      }
    },
    columns() {
      if (!this.individualResponses.length) return [];

      const keys = Object.keys(this.individualResponses[0]);
      const columns = keys
          .filter(key => key !== 'id')
          .map(key => {
            const hasEmptyValues = this.individualResponses.some(response =>
                response[key] === null || response[key] === undefined || response[key] === ''
            );

            return {
              title: key.split('| id:')[0].trim(),
              dataIndex: key,
              key: key,
              width: 220,
              customHeaderCell: () => ({
                style: {
                  maxWidth: '220px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }
              }),
              customCell: () => ({
                style: {
                  maxWidth: '220px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }
              }),
              ...(hasEmptyValues && {
                filters: [
                  {text: 'Show Non-Empty', value: 'nonEmpty'},
                  {text: 'Show All', value: 'all'}
                ],
                filteredValue: this.filteredInfo?.[key] || null,
                onFilter: (value, record) => {
                  if (value === 'nonEmpty') {
                    return record[key] !== null && record[key] !== undefined && record[key] !== '';
                  }
                  return true;
                }
              }),
              ...(key === 'Submitted at' && {
                sorter: (a, b) => new Date(a['Submitted at']) - new Date(b['Submitted at'])
              })
            };
          });

      // Move "Submitted at" to the front
      const submittedAtIndex = columns.findIndex(col => col.key === 'Submitted at');
      if (submittedAtIndex > -1) {
        const [submittedAt] = columns.splice(submittedAtIndex, 1);
        columns.unshift(submittedAt);
      }

      return columns;
    },
    tableData() {
      return this.individualResponses.map(response => ({
        ...response
      }));
    },
    pagination: function () {
      return {
        total: this.individualResponses.length,
        pageSize: 20,
        showSizeChanger: true,
        pageSizeOptions: ["20", "50", "100"],
      };
    }
  },
  methods: {
    showEmptyData() {
      return <a-empty class="pt-12 pb-20">
        <EmptyTableSvg slot="image"/>
        <span slot="description" class="text-neutral_gray-300 text-2xl">No data</span>
      </a-empty>
    },
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },
    handleChange(pagination, filters) {
      this.filteredInfo = filters;
    },
    clearFilters() {
      this.filteredInfo = null;
    },
    onDeleteResults() {
      this.$confirm({
        title: 'Delete selected responses?',
        content: 'This action cannot be undone.',
        okText: 'Delete',
        cancelText: 'No, cancel',
        okButtonProps: {props: {type: 'danger', size: 'default'}},
        centered: true,
        maskClosable: true,
        class: 'modal-delete-survey',
        icon: () => (<a-icon type="warning" style="color: var(--color-negative)"/>),
        onOk: () => {
          this.isDeletingResults = true
          this.$store.dispatch(`survey/${RESET_RESULTS}`, {
            survey_id: this.$route.params.id,
            completionIds: this.selectedRowKeys,
          })
              .finally(() => {
                this.selectedRowKeys = []
                this.isDeletingResults = false
              })
        },
      })
    }
  }
}
</script>

<style scoped>

</style>
